import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon, Business as BusinessIcon } from '@mui/icons-material';
import { debounce } from 'lodash';
import axios from 'axios';
import { log } from '../../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export interface Sector {
  id: string;
  slug: string;
  name: string;
  summary80: string;
}

export interface SectorSelectProps {
  value?: Sector | null;
  onChange: (sector: Sector | null) => void;
  onBlur?: () => void;
  error?: boolean;
  helperText?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  margin?: 'none' | 'dense' | 'normal';
  autoFocus?: boolean;
  disableClearable?: boolean;
  className?: string;
  sx?: any;
}

/**
 * SectorSelect component for searching and selecting business sectors
 * Uses Material-UI Autocomplete with debounced API search
 */
export const SectorSelect: React.FC<SectorSelectProps> = ({
  value = null,
  onChange,
  onBlur,
  error = false,
  helperText,
  label = 'Business Sector',
  placeholder = 'Search for your industry...',
  required = false,
  disabled = false,
  size = 'medium',
  fullWidth = true,
  variant = 'outlined',
  margin = 'normal',
  autoFocus = false,
  disableClearable = false,
  className,
  sx,
}) => {
  const [inputValue, setInputValue] = useState(value?.name || '');
  const [options, setOptions] = useState<Sector[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // Debounced search that hits the backend for sector matches
  const debouncedSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        if (!query || query.trim().length < 2) {
          setOptions([]);
          setLoading(false);
          return;
        }

        setLoading(true);
        try {
          log.api.request('GET', '/sectors/search', { query });

          const response = await axios.get(`${API_URL}/api/v1/sectors/search`, {
            params: { query },
            withCredentials: true,
          });

          const sectors: Sector[] = response.data?.sectors ?? [];
          setOptions(sectors);
          log.api.response('GET', '/sectors/search', response.status, response.data);
        } catch (fetchError) {
          console.error('Error fetching sectors:', fetchError);
          log.error('Error fetching sectors', fetchError);
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, 300),
    []
  );

  const fetchSectors = useCallback(
    (query: string) => {
      debouncedSearch(query);
    },
    [debouncedSearch]
  );

  useEffect(() => {
    if (open) {
      fetchSectors(inputValue);
    }
  }, [open, inputValue, fetchSectors]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Keep input in sync when value changes externally
  useEffect(() => {
    if (value && value.name !== inputValue) {
      setInputValue(value.name);
    }
    if (!value && !open) {
      setInputValue('');
    }
  }, [value, inputValue, open]);

  const handleChange = useCallback(
    (_event: any, newValue: Sector | null) => {
      onChange(newValue);
    },
    [onChange]
  );

  useEffect(() => {
    if (value) {
      log.action('sector-select', {
        sector: value.name,
        sectorId: value.id,
      });
    }
  }, [value]);

  const renderOption = (props: any, option: Sector) => {
    const { key, ...otherProps } = props;
    return (
      <Box component="li" key={key} {...otherProps}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <BusinessIcon
            sx={{
              mt: 0.5,
              color: 'text.secondary',
              fontSize: 20,
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {option.name}
            </Typography>
            {option.summary80 && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  mt: 0.25,
                }}
              >
                {option.summary80}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const getOptionLabel = (option: Sector | string) => {
    if (typeof option === 'string') return option;
    return option?.name || '';
  };

  const isOptionEqualToValue = (option: Sector, selected: Sector) => {
    return option.id === selected.id || option.slug === selected.slug;
  };

  return (
    <Autocomplete
      className={className}
      sx={sx}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
        if (open) {
          fetchSectors(newInputValue);
        }
      }}
      options={options}
      loading={loading}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderOption}
      fullWidth={fullWidth}
      size={size}
      disabled={disabled}
      disableClearable={disableClearable}
      autoHighlight
      openOnFocus
      blurOnSelect
      filterOptions={(x) => x}
      noOptionsText={
        inputValue.trim().length < 2
          ? 'Type at least 2 characters to search'
          : 'No sectors found'
      }
      loadingText="Searching sectors..."
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant={variant}
          margin={margin}
          required={required}
          error={error}
          helperText={helperText}
          autoFocus={autoFocus}
          onBlur={onBlur}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SectorSelect;
